import dayjs from 'dayjs';
import {User} from '../types';

// The ONE place Directory decides what it sends to users-api on create and
// update. Every write goes through here; the form never builds the input itself.
//
// ⚠️ ONLY THE FIELDS BELOW ARE WRITTEN. In particular the Account link
// (`accountId`, stored as custom:account_id) and the Account Admin flag
// (`accountAdmin`) are NOT in this list, and that is the point. Directory shows
// both and never writes them (FNP-2015, FNP-1994). The console's copy sent the
// whole record, so every save rewrote the person's Account link.
//
// ⚠️ OMIT, NEVER NULL. users-api skips an attribute whose value is undefined
// and turns null into '', which CLEARS it in Cognito. A field left out here is
// left alone; a field sent as null is wiped.
const EDITABLE_FIELDS = [
    'address',
    'email',
    'familyName',
    'gender',
    'givenName',
    'locale',
    'middleName',
    'name',
    'nickname',
    'phoneNumber',
    'picture',
    'profile',
    'website',
    'zoneInfo',
] as const;

type EditableField = typeof EDITABLE_FIELDS[number];

export type UserInput = Partial<Pick<User, EditableField>> & {
    username: string,
    birthDate?: string,
};

const pickEditable = (values: User) => {
    const picked: Partial<Pick<User, EditableField>> = {};
    EDITABLE_FIELDS.forEach((field) => {
        if (typeof values[field] !== 'undefined') {
            picked[field] = values[field];
        }
    });
    return picked;
};

// The console formatted `moment(values.birthDate)` unguarded, and an undefined
// date is NOW: editing anyone without a birth date wrote today's date as theirs.
//
// A birth date is a calendar date, not an instant. An ISO string keeps its own
// date part ("1815-12-10T00:00:00Z" is the 10th, not the 9th in Pacific time);
// a Date from a date picker is local midnight, so it formats in local time.
const ISO_DATE = /^(\d{4}-\d{2}-\d{2})/;

const birthDateOf = (values: User) => {
    const raw = values.birthDate as unknown;
    if (typeof raw === 'string' && ISO_DATE.test(raw)) {
        return {birthDate: (raw.match(ISO_DATE) as RegExpMatchArray)[1]};
    }
    if (raw instanceof Date && dayjs(raw).isValid()) {
        return {birthDate: dayjs(raw).format('YYYY-MM-DD')};
    }
    return {};
};

export const toUpdateInput = (values: User): UserInput => ({
    ...pickEditable(values),
    ...birthDateOf(values),
    username: values.username as string,
});

// The pool signs in by email (UsernameAttributes: email, phone_number), so
// Cognito takes the email as the username at create and assigns its own id.
export const toCreateInput = (values: User): UserInput => ({
    ...pickEditable(values),
    ...birthDateOf(values),
    username: values.email as string,
});
